import objectHasKeys from '@/utils/object-has-keys'
import ErrorResponseType from '@/shared/constants/error/ErrorResponseType'
import RequestErrorSource from '@/shared/constants/error/RequestErrorSource'
import ErrorResponse from '@/models/error/ErrorResponse'
import { isEmpty } from 'lodash'

// Utils

const determineRequestErrorType = (error) => {
  if (error?.response) return RequestErrorSource.server
  else if (error?.request) return RequestErrorSource.request
  else return RequestErrorSource.unknown
}

/**
 * Default API request validation response
 *
 * Object Structure:
 * ```json
 * {
    errors: Object,
    status: Number,
    title: String?,
    traceId: String?,
    type: String?,
  }
 * ```
 *
 * @param {Object} responseData
 * @returns
 */
const mapApiValidationErrorResponseToError = (error, errorSource, $i18n) => {
  const base = baseErrorResponse(error, errorSource, $i18n)
  base.data = error.response?.data?.errors
  base.type = error.response?.data?.type || ErrorResponseType.api
  base.message = error.response?.data?.title || base.message

  return base
}

/**
 * Ready2WorkAPI.DTOs.V1.Core.ErrorResponse
 *
 * Object Structure:
 * ```json
 * {
    type: String?,
    code: String?,
    message: String?,
    param: String?,
  }
 * ```
 *
 * @returns
 */
const mapR2WErrorResponseToError = (error, errorSource, $i18n) => {
  const base = baseErrorResponse(error, errorSource, $i18n)
  base.type = error.response?.data?.type || ErrorResponseType.api
  base.code = error.response?.data?.code || base.code
  base.message = error.response?.data?.message || base.message
  base.param = error.response?.data?.param
  return base
}

// Constants
const BASE_ERROR_RESPONSE_OBJ_KEYS = ['type', 'code', 'param']
const API_VALIDATION_ERROR_OBJ_KEYS = [
  'errors',
  'status',
  'title',
  'traceId',
  'type',
]

// Abstract Product
const baseErrorResponse = (error, errorSource, $i18n) => {
  return new ErrorResponse({
    _error: error,
    source: errorSource,
    type: ErrorResponseType.unknown,
    message: $i18n.t('error.genericApiError'),
  })
}

// Factory
export default function(error, $i18n) {
  const errorSource = determineRequestErrorType(error)

  switch (errorSource) {
    case RequestErrorSource.server:
      return serverErrorResponse(error, errorSource, $i18n)
    case RequestErrorSource.request:
      return requestErrorResponse(error, errorSource, $i18n)
    default:
      return baseErrorResponse(error, errorSource, $i18n)
  }
}

// Concrete Products
const serverErrorResponse = (error, errorSource, $i18n) => {
  if (!error?.response || isEmpty(error?.response) || !error?.response?.data)
    return baseErrorResponse(error, errorSource, $i18n)

  // Case: Default API request validation response
  if (objectHasKeys(API_VALIDATION_ERROR_OBJ_KEYS, error.response?.data)) {
    return mapApiValidationErrorResponseToError(error, errorSource, $i18n)
  }

  // Case: Ready2WorkAPI.DTOs.V1.Core.ErrorResponse
  if (objectHasKeys(BASE_ERROR_RESPONSE_OBJ_KEYS, error.response?.data))
    return mapR2WErrorResponseToError(error, errorSource, $i18n)

  // Default Case
  return baseErrorResponse(error, errorSource, $i18n)
}

const requestErrorResponse = (error, errorSource, $i18n) => {
  return baseErrorResponse(error, errorSource, $i18n)
}
